'use client';

/**
 * Generic caching service for repository implementations
 * Provides in-memory caching with TTL and size limits
 */

export interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  maxSize?: number; // Maximum number of cache entries
  keyPrefix?: string; // Prefix for cache keys
}

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

export interface CacheStats {
  hits: number;
  misses: number;
  size: number;
  hitRate: number;
}

/**
 * In-memory cache implementation with TTL and LRU eviction
 */
export class MemoryCache<T = unknown> {
  private cache = new Map<string, CacheEntry<T>>();
  private accessOrder = new Map<string, number>(); // For LRU tracking
  private stats = { hits: 0, misses: 0 };
  private accessCounter = 0;

  constructor(private options: CacheOptions = {}) {
    this.options = {
      ttl: 5 * 60 * 1000, // Default 5 minutes
      maxSize: 100, // Default max 100 entries
      keyPrefix: '',
      ...options,
    };
  }

  /**
   * Get value from cache
   */
  get(key: string): T | null {
    const fullKey = this.getFullKey(key);
    const entry = this.cache.get(fullKey);

    if (!entry) {
      this.stats.misses++;
      return null;
    }

    // Check if entry has expired
    if (this.isExpired(entry)) {
      this.cache.delete(fullKey);
      this.accessOrder.delete(fullKey);
      this.stats.misses++;
      return null;
    }

    // Update access order for LRU
    this.accessOrder.set(fullKey, ++this.accessCounter);
    this.stats.hits++;

    return entry.data;
  }

  /**
   * Set value in cache
   */
  set(key: string, value: T, customTtl?: number): void {
    const fullKey = this.getFullKey(key);
    const ttl = customTtl || this.options.ttl!;

    const entry: CacheEntry<T> = {
      data: value,
      timestamp: Date.now(),
      ttl,
    };

    this.cache.set(fullKey, entry);
    this.accessOrder.set(fullKey, ++this.accessCounter);

    // Evict oldest entries if cache is full
    this.evictIfNeeded();
  }

  /**
   * Remove value from cache
   */
  delete(key: string): boolean {
    const fullKey = this.getFullKey(key);
    const deleted = this.cache.delete(fullKey);
    this.accessOrder.delete(fullKey);
    return deleted;
  }

  /**
   * Check if key exists in cache and is not expired
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Clear all entries from cache
   */
  clear(): void {
    this.cache.clear();
    this.accessOrder.clear();
    this.stats = { hits: 0, misses: 0 };
    this.accessCounter = 0;
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    const { hits, misses } = this.stats;
    const total = hits + misses;
    const hitRate = total === 0 ? 0 : hits / total;

    return {
      hits,
      misses,
      size: this.cache.size,
      hitRate,
    };
  }

  /**
   * Get or set pattern - useful for repository implementations
   */
  async getOrSet<R>(key: string, getter: () => Promise<R>, customTtl?: number): Promise<R> {
    const cached = this.get(key);
    if (cached !== null) {
      return cached as unknown as R;
    }

    const value = await getter();
    this.set(key, value as unknown as T, customTtl);
    return value;
  }

  /**
   * Invalidate entries by pattern (prefix matching)
   */
  invalidateByPattern(pattern: string): number {
    let count = 0;
    const regex = new RegExp(pattern);

    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
        this.accessOrder.delete(key);
        count++;
      }
    }

    return count;
  }

  private getFullKey(key: string): string {
    return this.options.keyPrefix ? `${this.options.keyPrefix}:${key}` : key;
  }

  private isExpired(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp > entry.ttl;
  }

  private evictIfNeeded(): void {
    const maxSize = this.options.maxSize!;
    if (this.cache.size <= maxSize) {
      return;
    }

    // Remove expired entries first
    this.cleanExpired();

    // If still over limit, remove least recently used entries
    while (this.cache.size > maxSize) {
      const oldestKey = this.getLeastRecentlyUsedKey();
      if (oldestKey) {
        this.cache.delete(oldestKey);
        this.accessOrder.delete(oldestKey);
      } else {
        break; // Safety break
      }
    }
  }

  private cleanExpired(): void {
    for (const [key, entry] of this.cache.entries()) {
      if (this.isExpired(entry)) {
        this.cache.delete(key);
        this.accessOrder.delete(key);
      }
    }
  }

  private getLeastRecentlyUsedKey(): string | null {
    let oldestKey: string | null = null;
    let oldestAccess = Infinity;

    for (const [key, access] of this.accessOrder.entries()) {
      if (access < oldestAccess) {
        oldestAccess = access;
        oldestKey = key;
      }
    }

    return oldestKey;
  }
}

/**
 * Cache service factory for creating configured cache instances
 */
export class CacheService {
  private static caches = new Map<string, MemoryCache>();

  /**
   * Get or create a cache instance with specific configuration
   */
  static getCache<T>(name: string, options?: CacheOptions): MemoryCache<T> {
    if (!this.caches.has(name)) {
      const cache = new MemoryCache<T>(options);
      this.caches.set(name, cache);
      return cache;
    }

    return this.caches.get(name)! as MemoryCache<T>;
  }

  /**
   * Get cache statistics for all named caches
   */
  static getAllStats(): Record<string, CacheStats> {
    const stats: Record<string, CacheStats> = {};
    for (const [name, cache] of this.caches.entries()) {
      stats[name] = cache.getStats();
    }
    return stats;
  }

  /**
   * Clear all caches
   */
  static clearAll(): void {
    for (const cache of this.caches.values()) {
      cache.clear();
    }
  }

  /**
   * Remove a specific cache
   */
  static removeCache(name: string): boolean {
    const cache = this.caches.get(name);
    if (cache) {
      cache.clear();
      this.caches.delete(name);
      return true;
    }
    return false;
  }
}

/**
 * Decorator for caching method results
 * Usage: @cached('methodName', { ttl: 10000 })
 */
export function cached(cacheKey?: string, options?: CacheOptions) {
  return function <R>(
    target: Record<string, unknown>,
    propertyName: string,
    descriptor: TypedPropertyDescriptor<(...args: unknown[]) => Promise<R>>,
  ) {
    const method = descriptor.value!;
    const cacheName = `${target.constructor.name}_${propertyName}`;

    descriptor.value = async function (...args: unknown[]): Promise<R> {
      const cache = CacheService.getCache<R>(cacheName, options);
      const key = cacheKey || `${propertyName}_${JSON.stringify(args)}`;

      return cache.getOrSet(key, async () => {
        return method.apply(this, args);
      });
    };

    return descriptor;
  };
}
